import { useState } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Radio, Text } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';

import { getLoanDurationInMonths } from '../../../../../myOffers/MyOffers.helpers';
import { ChooseDuration } from '../ChooseDuration';
import { formatDurations } from '../formatDurations';

type TSelectedOption = 'interest_only' | 'fresh_duration' | null;

const getDefaultSelectedOption = (
    selectedOffer: TFormalOffer | null
): TSelectedOption => {
    if (!selectedOffer) return null;

    if (getIsInterestOnlyOffer(selectedOffer)) return 'interest_only';

    if (getIsFreshDurationOffer(selectedOffer)) return 'fresh_duration';

    return null;
};

const getIsFreshDurationOffer = (offer: TFormalOffer) => {
    return offer.is_repayment_reduction && !getIsInterestOnlyOffer(offer);
};

export const LoanExtensionRadioGroup = ({
    offers,
    selectedOffer,
    setSelectedOffer,
    outstandingBalance
}: {
    offers: TFormalOffer[];
    selectedOffer: TFormalOffer | null;
    setSelectedOffer: (offer: TFormalOffer | null) => void;
    outstandingBalance: number;
}) => {
    const [selectedOption, setSelectedOption] = useState<TSelectedOption>(
        getDefaultSelectedOption(selectedOffer)
    );

    const interestOnlyOffer = offers.find((offer) =>
        getIsInterestOnlyOffer(offer)
    );

    const freshDurationOffers = offers.filter((offer) =>
        getIsFreshDurationOffer(offer)
    );

    const chooseInterestOnly = (interestOnlyFormalOffer: TFormalOffer) => {
        setSelectedOption('interest_only');
        setSelectedOffer(interestOnlyFormalOffer);
    };

    const chooseFreshDuration = (freshDurationFormalOffers: TFormalOffer[]) => {
        setSelectedOption('fresh_duration');

        if (freshDurationFormalOffers.length > 1) {
            setSelectedOffer(null);
            return;
        }

        setSelectedOffer(freshDurationFormalOffers[0]);
    };

    if (interestOnlyOffer)
        return (
            <div className="space-y-xl">
                <div className="space-y-m">
                    <Text className="text-xl font-bold">
                        <FormattedMessage
                            id="_.topup_choice.how_would_you_like_to_extend_your_loan"
                            defaultMessage="How would you like to extend your loan?"
                        />
                    </Text>
                    <div className="flex flex-col gap-m min-sm:flex-row">
                        <InterestOnlyRadioChoice
                            interestOnlyOffer={interestOnlyOffer}
                            selectedOffer={selectedOffer}
                            setSelectedOffer={chooseInterestOnly}
                        />
                        <FreshDurationRadioChoice
                            freshDurationOffers={freshDurationOffers}
                            selectedOption={selectedOption}
                            chooseFreshDuration={chooseFreshDuration}
                            outstandingBalance={outstandingBalance}
                        />
                    </div>
                </div>

                {selectedOption === 'fresh_duration' &&
                freshDurationOffers.length > 1 ? (
                    <ChooseDuration
                        offers={freshDurationOffers}
                        selectedOffer={selectedOffer}
                        setSelectedOffer={setSelectedOffer}
                    />
                ) : null}
            </div>
        );

    return (
        <ChooseDuration
            offers={freshDurationOffers}
            selectedOffer={selectedOffer}
            setSelectedOffer={setSelectedOffer}
        />
    );
};

const InterestOnlyRadioChoice = ({
    interestOnlyOffer,
    selectedOffer,
    setSelectedOffer
}: {
    interestOnlyOffer: TFormalOffer;
    selectedOffer: TFormalOffer | null;
    setSelectedOffer: (formalOffer: TFormalOffer) => void;
}) => {
    return (
        <Radio.Root
            variant="frame"
            id="interest-only"
            className="flex-1 items-start"
        >
            <Radio.Label data-testid="interest-only-label">
                <FormattedMessage
                    id="_.topup_choice.add_3_months_reduced_repayment_period"
                    defaultMessage="Add a 3 month reduced repayment period"
                />
                <span className="block text-s font-reg">
                    <FormattedMessage
                        id="_.topup_choice.add_3_months_reduced_repayment_period_description"
                        defaultMessage="Pay only interest for 3 months, then repay your balance over {loanDuration} months"
                        values={{
                            loanDuration:
                                getLoanDurationInMonths(
                                    interestOnlyOffer!.duration!
                                ) - 3
                        }}
                    />
                </span>
            </Radio.Label>
            <Radio.Input
                name="topup-choice"
                onChange={() => setSelectedOffer(interestOnlyOffer)}
                checked={selectedOffer === interestOnlyOffer}
            />
        </Radio.Root>
    );
};

const FreshDurationRadioChoice = ({
    freshDurationOffers,
    selectedOption,
    chooseFreshDuration,
    outstandingBalance
}: {
    freshDurationOffers: TFormalOffer[];
    selectedOption: TSelectedOption;
    chooseFreshDuration: (formalOffer: TFormalOffer[]) => void;
    outstandingBalance: number;
}) => {
    const intl = useIntl();

    return (
        <Radio.Root
            variant="frame"
            id="reduce-minimum-repayments"
            className="flex-1 items-start"
        >
            <Radio.Label data-testid="fresh-duration-label">
                <FormattedMessage
                    id="_.topup_choice.fresh_new_duration"
                    defaultMessage="Fresh new duration starting from today"
                />
                <span className="block text-s font-reg">
                    <FormattedMessage
                        id="_.topup_choice.fresh_new_duration_description"
                        defaultMessage="Start a new agreement for {durations} months whilst keeping your {outstandingBalance} balance."
                        values={{
                            durations: formatDurations(
                                freshDurationOffers,
                                // @ts-expect-error intl.locale is typed as string
                                intl.locale
                            ),
                            outstandingBalance: (
                                <FormattedCurrency value={outstandingBalance} />
                            )
                        }}
                    />
                </span>
            </Radio.Label>
            <Radio.Input
                name="topup-choice"
                onChange={() => chooseFreshDuration(freshDurationOffers)}
                checked={selectedOption === 'fresh_duration'}
            />
        </Radio.Root>
    );
};
